import { getPathName } from 'features/plc/contracts/pressHMI.schema';
import { usePLCValue, usePLCWriteState } from 'features/plc/providers/PLCProvider';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import PLCNumberInput from './PLCNumberInput';

const formatFieldValue = (value, type) => {
  if (value === undefined || value === null) {
    return '';
  }

  if (type === 'TIME') {
    return String(value / 1000);
  }

  return String(value);
};

const parseFieldValue = (value, type) => {
  if (type === 'TIME') {
    return Math.round(Number.parseFloat(value) * 1000);
  }

  if (['DINT', 'INT', 'UINT'].includes(type)) {
    return Number.parseInt(value, 10);
  }

  return Number.parseFloat(value);
};

function PLCNumberEditor({ field }) {
  const { activeWrite, runWrite, setWriteError } = usePLCWriteState();
  const plcValue = usePLCValue(field.path);
  const handle = getPathName(field.path);
  const label = useMemo(() => {
    if (field.path.type !== 'TIME' || field.label.endsWith('(Seconds)')) {
      return field.label;
    }

    return `${field.label} (Seconds)`;
  }, [field.label, field.path.type]);

  const inputType = useMemo(
    () => (['DINT', 'INT', 'UINT'].includes(field.path.type) ? 'int' : 'float'),
    [field.path.type],
  );
  const parsedDisplayValue = Number.parseFloat(formatFieldValue(plcValue, field.path.type));
  const rangeError = Number.isFinite(parsedDisplayValue)
    && ((field.min !== undefined && parsedDisplayValue < field.min)
      || (field.max !== undefined && parsedDisplayValue > field.max))
    ? `${field.label} must be between ${field.min ?? 'no minimum'} and ${field.max ?? 'no maximum'}`
    : '';

  const handleUpdate = async (nextValue) => {
    const parsedValue = parseFieldValue(String(nextValue), field.path.type);

    if (Number.isNaN(parsedValue)) {
      setWriteError(`Invalid value for ${field.label}`);
      return;
    }

    if (field.min !== undefined && parsedValue < field.min) {
      setWriteError(`${field.label} must be at least ${field.min}`);
      return;
    }

    if (field.max !== undefined && parsedValue > field.max) {
      setWriteError(`${field.label} must be ${field.max} or less`);
      return;
    }

    await runWrite({ path: field.path, value: parsedValue });
  };

  return (
    <PLCNumberInput
      label={label}
      description={field.description}
      errorText={rangeError}
      max={field.max}
      min={field.min}
      value={formatFieldValue(plcValue, field.path.type)}
      disabled={field.disabled || activeWrite === handle}
      type={inputType}
      onUpdate={handleUpdate}
    />
  );
}

PLCNumberEditor.propTypes = {
  field: PropTypes.shape({
    description: PropTypes.string,
    disabled: PropTypes.bool,
    label: PropTypes.string.isRequired,
    max: PropTypes.number,
    min: PropTypes.number,
    path: PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default PLCNumberEditor;
